import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {type Ref} from 'src/state/views/filter/types';

import * as FilterActions from '../../state/views/filter/actions';
import {type PanelSelections} from './../../util/selectionmanager';

/**
 * Notes on the non-standard pattern here. Typically we use the `ViewHooks.useViewAction` helper for dispatching these kinds of actions. However, we have the issue here of needing to apply this change to a variable number of runsets. The "Rules of Hooks" prevent us from writing code that would vary the number of times a hook is called across renders, so we can't do anything like the following because it would violate the rule.
 * ```
 * // pseudocode- not actually correct
 * const convertSelectionToFiltersCallback = useCallback(() => {
 *  runSets.forEach(runset => ViewHooks.useViewAction(runset.filtersRef, FilterActions.selectionsToFilters))
 * }, [ columnSelections, runSets])
 * ```
 * We might be able to do something like this if we refactored `dispatch` out of the helper (so it stopped being a hook) but this is more complexity than desired. Full notes on the use case behind this change: https://www.notion.so/wandbai/Filtering-the-parallel-coordinates-plot-in-a-report-abf2d6b5686247bd8c960f8b25bc9a6a?pvs=4
 */
export function useConvertSelectionsToFilters(
  columnSelections: PanelSelections,
  runsetRefs: Ref[]
) {
  const dispatch = useDispatch();

  const convertSelectionsToFilters = useCallback(
    (axis: string) => {
      runsetRefs.forEach(rsRef => {
        dispatch(
          FilterActions.selectionsToFilters(rsRef, columnSelections, [axis])
        );
      });
    },
    [columnSelections, dispatch, runsetRefs]
  );

  return convertSelectionsToFilters;
}
